import NotificationUnreadDot from "@/components/NotificationUnreadDot";
import { Link as NotificationHistoryLink } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth";
import { Leaf, Home, Calculator, Boxes, FileText, LogOut } from "lucide-react";

const TABS = [
  { to: "/", label: "Home", icon: Home, tid: "nav-home", match: p => p === "/" },
  { to: "/calculator", label: "Calculator", icon: Calculator, tid: "nav-calc", match: p => p.startsWith("/calculator") },
  { to: "/catalog", label: "Catalog", icon: Boxes, tid: "nav-catalog", match: p => p.startsWith("/catalog") },
  { to: "/quotes", label: "Quotes", icon: FileText, tid: "nav-quotations", match: p => p.startsWith("/quotes") || p.startsWith("/quotations") },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const roleLabel = user.role.charAt(0).toUpperCase() + user.role.slice(1);
  const [testing, setTesting] = useState(false);
  const [staffBlocked, setStaffBlocked] = useState(false);
  useEffect(() => {
    let mounted = true;
    const update = () => api.get("/admin/live-testing").then(({data}) => {
      if (!mounted) return;
      setTesting(data.active && data.eligible);
      setStaffBlocked(!["inactive", "failed"].includes(data.state) && !data.eligible);
    }).catch(() => {});
    update();
    const interval = setInterval(update, 5000);
    const onChange = () => update();
    window.addEventListener("fc-testing-changed", onChange);
    return () => { mounted = false; clearInterval(interval); window.removeEventListener("fc-testing-changed", onChange); };
  }, []);
  useEffect(() => {
    if (!testing) return;
    let lastSent = 0;
    const activity = () => {
      const now = Date.now();
      if (now - lastSent < 60000) return;
      lastSent = now;
      api.post("/admin/live-testing/activity").catch(() => {});
    };
    document.addEventListener("pointerdown", activity, true);
    document.addEventListener("keydown", activity, true);
    document.addEventListener("scroll", activity, true);
    return () => {
      document.removeEventListener("pointerdown", activity, true);
      document.removeEventListener("keydown", activity, true);
      document.removeEventListener("scroll", activity, true);
    };
  }, [testing]);

  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <div className="min-h-screen bg-[#FCFBF8]">
      {testing && <div role="alert" className="sticky top-0 z-50 bg-red-700 text-white text-center px-3 py-2 text-sm font-bold animate-pulse">
        LIVE TESTING MODE — Admin changes are temporary and will be discarded when testing ends. Staff access is paused.
      </div>}
      {staffBlocked && <div role="alert" className="fixed inset-0 z-[100] bg-white flex items-center justify-center p-8 text-center text-red-800 font-semibold">Live app testing is in progress. Staff access will resume when the administrator ends testing.</div>}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200/80">
        <div className={`${isAdminRoute ? "max-w-xl xl:max-w-[1540px] xl:px-8 2xl:px-10" : "max-w-xl"} mx-auto px-4 h-16 flex items-center justify-between`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <Leaf className="w-4 h-4 text-green-600" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 leading-tight truncate">Freshcare Business Solutions</div>
              <div className="text-xs text-slate-500 truncate">{user.name} · {roleLabel}</div>
            </div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-button"
            className="flex items-center gap-1.5 text-sm text-slate-700 border border-slate-300 rounded-full px-3.5 py-1.5 hover:bg-slate-50 transition-colors shrink-0"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className={`${isAdminRoute ? "max-w-xl xl:max-w-[1600px] xl:px-8 xl:pt-8 xl:pb-12 2xl:px-10" : "max-w-xl"} mx-auto px-4 pt-6 pb-28`}>
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/");
          }}
          data-testid="global-back-button"
          className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 active:bg-slate-100"
        >
          <span aria-hidden="true" className="text-base leading-none">←</span>
          Back
        </button>
<div className="mb-4 flex justify-end" data-testid="notification-history-shortcut"><NotificationHistoryLink to="/notification-history" className="relative inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 shadow-sm hover:bg-blue-50">Notification History<NotificationUnreadDot /></NotificationHistoryLink></div>
        {children}
      </main>

      <nav className={`${isAdminRoute ? "xl:hidden" : ""} fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200`}>
        <div className="max-w-xl mx-auto grid grid-cols-4">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = t.match(location.pathname);
            return (
              <Link key={t.to} to={t.to} data-testid={t.tid}
                className={`flex flex-col items-center justify-center gap-1 h-16 text-[11px] font-medium transition-colors ${active ? "text-blue-600" : "text-slate-500"}`}>
                <Icon className="w-5 h-5" strokeWidth={active ? 2.4 : 2} />
                {t.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

// freshcare-notification-link-v1

// freshcare-unread-dot-v1
